import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select } from "../components/UI";
import { toast } from "sonner";
import { QrCode, Plus, Trash2, Loader2, Copy, ScanLine } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHANNELS = ["event", "direct_mail", "yard_sign", "poster", "ctv", "fundraising", "volunteer_signup", "website", "email"];

export default function QrCodes() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", channel: "event", target_url: "" });

  const load = () => api.get("/political-os/qr").then(r => setData(r.data)).catch(() => setData({ qr_codes: [] }));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.target_url.trim()) return toast.error("Name and target URL are required");
    try { await api.post("/political-os/qr", form); toast.success("QR code created"); setForm({ ...form, name: "", target_url: "" }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not create QR code")); }
  };
  const remove = async (id) => { try { await api.delete(`/political-os/qr/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const copyLink = (code) => navigator.clipboard?.writeText(`${BACKEND}/api/public/qr/${code}`).then(() => toast.success("Tracking link copied")).catch(() => {});

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="qr-codes-page">
      <PageHeader eyebrow="Attribution" title="TotalReach QR"
        subtitle="Trackable QR codes for events, mailers, signs and CTV — every scan is counted by channel and day." />
      <Card data-testid="create-qr-card">
        <div className="flex flex-wrap gap-3 items-end">
          <Input label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="October mailer — donate" className="flex-1 min-w-[200px]" data-testid="qr-name-input" />
          <Select label="Channel" value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))} data-testid="qr-channel-select">
            {CHANNELS.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </Select>
          <Input label="Target URL" value={form.target_url} onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))}
            placeholder="https://smithforcongress.com/donate" className="flex-1 min-w-[220px]" data-testid="qr-target-input" />
          <Btn onClick={create} data-testid="create-qr-btn"><Plus className="w-4 h-4" /> Generate</Btn>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.qr_codes.map(qr => (
          <Card key={qr.id} data-testid={`qr-card-${qr.id}`}>
            <div className="flex gap-4">
              <img src={`${BACKEND}/api/public/qr/${qr.code}/image`} alt={`QR for ${qr.name}`}
                className="w-24 h-24 rounded-lg bg-white p-1 shrink-0" data-testid={`qr-image-${qr.id}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate">{qr.name}</div>
                <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-cyan-400/30 text-cyan-300 inline-block mt-1">{qr.channel.replace(/_/g, " ")}</span>
                <div className="text-[10px] text-slate-500 mt-1.5 truncate">→ {qr.target_url}</div>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1"><ScanLine className="w-3.5 h-3.5 text-cyan-300/70" /> <b data-testid={`qr-scans-${qr.id}`}>{qr.scans}</b> scans</span>
                  <span className="text-slate-500">{qr.scans_14d} in 14d</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => copyLink(qr.code)} data-testid={`qr-copy-${qr.id}`}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:border-cyan-400/40 transition-colors inline-flex items-center gap-1.5"><Copy className="w-3 h-3" /> Copy tracking link</button>
              <a href={`${BACKEND}/api/public/qr/${qr.code}/image`} download={`${qr.name}.png`}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:border-cyan-400/40 transition-colors">Download PNG</a>
              <button onClick={() => remove(qr.id)} className="text-slate-600 hover:text-red-400 ml-auto"><Trash2 className="w-4 h-4" /></button>
            </div>
            {qr.last_scan_at && <div className="text-[10px] text-slate-600 mt-2">Last scan {qr.last_scan_at.slice(0, 16).replace("T", " ")} UTC</div>}
          </Card>
        ))}
        {data.qr_codes.length === 0 && <Card><div className="text-sm text-slate-500 py-4 text-center">No QR codes yet — generate your first above.</div></Card>}
      </div>
    </div>
  );
}
